/**
 * AIEventsContext — real-time AI provider event streaming via SSE.
 *
 * Uses fetch() + ReadableStream instead of native EventSource because the
 * SSE endpoint requires an X-Organization-ID header that EventSource cannot send.
 *
 * Connection lifecycle:
 *  - Opens on mount (when org ID is available in localStorage).
 *  - Reconnects after 5 s on any connection drop.
 *  - Closes cleanly on unmount.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { fetchAIEvents } from '../api/aiEventsApi.ts';
import { performTokenRefresh } from '../api/oauth/refreshFlow.ts';

export interface AIEvent {
  id: number;
  org_id: string;
  provider: string;
  event_type: string;
  severity: 'error' | 'warning' | 'info';
  api_endpoint?: string;
  http_status?: number;
  error_message?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
}

interface AIEventsContextValue {
  events: AIEvent[];
  unreadCount: number;
  clearUnread: () => void;
  connected: boolean;
}

const AIEventsContext = createContext<AIEventsContextValue>({
  events: [],
  unreadCount: 0,
  clearUnread: () => {},
  connected: false,
});

const MAX_EVENTS = 200; // keep last N events in memory
const RECONNECT_DELAY_MS = 5000;
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const LS_LAST_SEEN_KEY = 'ai_events_last_seen_id';

export function AIEventsProvider({ children }: { children: React.ReactNode }) {
  const [events, setEvents] = useState<AIEvent[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track the highest event id seen — populated from both the API seed on mount
  // AND from each incoming SSE event.  Decoupled from the events[] array so
  // clearUnread() works even when the SSE stream has not delivered anything yet.
  const latestEventIdRef = useRef<number>(
    parseInt(window.localStorage.getItem(LS_LAST_SEEN_KEY) || '0', 10)
  );

  // Seed unread count from API on mount so the badge survives page refreshes.
  // Compares each event's id against the last id the user acknowledged.
  //
  // Guard: only run when the user is authenticated. AIEventsProvider sits
  // outside <Router> so this effect fires even on /signin. Without the guard,
  // fetchAIEvents gets a 401 → the axios interceptor calls performTokenRefresh()
  // → refresh fails on unauthenticated pages → signOutAndRedirect() fires
  // window.location.replace(signin) → full reload → repeat = infinite loop.
  // The SSE connect() already has an identical guard below.
  useEffect(() => {
    const token = window.localStorage.getItem('access_token') || '';
    const orgId = window.localStorage.getItem('organization_id') || '';
    if (!token || !orgId) return; // not authenticated — skip API call, badge stays 0

    fetchAIEvents({ hours: 24, page_size: 200 })
      .then((res) => {
        const apiEvents = res.events ?? [];
        const lastSeenId = parseInt(
          window.localStorage.getItem(LS_LAST_SEEN_KEY) || '0',
          10
        );
        // Track the highest id in this API response so clearUnread() can persist it.
        const maxId = apiEvents.reduce((m, e) => Math.max(m, e.id), 0);
        if (maxId > latestEventIdRef.current) {
          latestEventIdRef.current = maxId;
        }
        const unseen = apiEvents.filter((e) => e.id > lastSeenId).length;
        if (unseen > 0) setUnreadCount(unseen);
      })
      .catch(() => {}); // best-effort — badge stays 0 if API is unreachable
  }, []);

  // When the user visits AI Activity and clears the badge, persist the highest
  // event id seen so a refresh doesn't re-show the same events as unread.
  // Uses latestEventIdRef — populated from the API seed AND from SSE — so this
  // works correctly even when the events[] state array is still empty.
  const clearUnread = useCallback(() => {
    setUnreadCount(0);
    try {
      const id = latestEventIdRef.current;
      if (id > 0) window.localStorage.setItem(LS_LAST_SEEN_KEY, String(id));
    } catch {
      // localStorage unavailable — ignore
    }
  }, []);

  const connect = useCallback(() => {
    // Only connect when the user is authenticated (both auth_user and org ID must be present)
    const orgId = window.localStorage.getItem('organization_id');
    const authUser = window.localStorage.getItem('auth_user');
    if (!orgId || !authUser) return;

    const controller = new AbortController();
    abortRef.current = controller;

    const url = `${API_BASE_URL}/api/ai-events/stream`;

    (async () => {
      try {
        // The app uses localStorage Bearer tokens (not cookies), so we must
        // pass Authorization here.  EventSource cannot send custom headers,
        // which is why we use fetch() + ReadableStream for this SSE endpoint.
        const token = window.localStorage.getItem('access_token') || '';
        const resp = await fetch(url, {
          signal: controller.signal,
          headers: {
            'Accept': 'text/event-stream',
            'Authorization': token ? `Bearer ${token}` : '',
            'X-Organization-Id': orgId,
          },
        });

        if (!resp.ok || !resp.body) {
          if (resp.status === 401) {
            // Token may have expired. Attempt a silent refresh (same mechanism as
            // axiosInstance) then reconnect. performTokenRefresh() signs the user
            // out if the refresh token has also expired, so no extra handling needed.
            setConnected(false);
            if (!controller.signal.aborted) {
              performTokenRefresh()
                .then(() => {
                  if (!controller.signal.aborted) {
                    reconnectTimerRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
                  }
                })
                .catch(() => {
                  // Refresh failed — user will be signed out by performTokenRefresh.
                });
            }
            return;
          }
          if (resp.status === 403) {
            // Authenticated but not authorised for this org — don't retry.
            setConnected(false);
            return;
          }
          throw new Error(`SSE response ${resp.status}`);
        }

        setConnected(true);

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // SSE frames end with \n\n
          const frames = buffer.split('\n\n');
          buffer = frames.pop() ?? '';

          for (const frame of frames) {
            if (!frame.trim()) continue;

            let eventType = '';
            let dataLine = '';
            for (const line of frame.split('\n')) {
              if (line.startsWith('event:')) {
                eventType = line.slice('event:'.length).trim();
              } else if (line.startsWith('data:')) {
                dataLine = line.slice('data:'.length).trim();
              }
            }

            if (eventType === 'ai_event' && dataLine) {
              try {
                const evt: AIEvent = JSON.parse(dataLine);
                // Keep the running high-water mark so clearUnread() can persist it.
                if (evt.id > latestEventIdRef.current) {
                  latestEventIdRef.current = evt.id;
                }
                setEvents((prev) => [evt, ...prev].slice(0, MAX_EVENTS));
                setUnreadCount((c) => c + 1);
              } catch {
                // malformed JSON — ignore
              }
            }
            // 'heartbeat' frames carry no data — just keep the connection alive
          }
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return; // intentional close
        // unexpected drop — schedule reconnect
      } finally {
        setConnected(false);
      }

      // Schedule reconnect (unless explicitly aborted)
      if (!controller.signal.aborted) {
        reconnectTimerRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
      }
    })();
  }, []);

  useEffect(() => {
    connect();
    return () => {
      // Tear down on unmount
      abortRef.current?.abort();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, [connect]);

  return (
    <AIEventsContext.Provider value={{ events, unreadCount, clearUnread, connected }}>
      {children}
    </AIEventsContext.Provider>
  );
}

export function useAIEventsContext(): AIEventsContextValue {
  return useContext(AIEventsContext);
}
